import { useCallback, useEffect, useRef, useState } from 'react';
import { criarSessaoAgora, mensagemDoErro } from './agoraSession';

// Liga o gestor de sessão (agoraSession.js) ao ciclo de vida do React.
//
// Dá ao ecrã três coisas que ele não tinha: um estado de ligação para mostrar
// ("a entrar" / "ligado" / "erro"), um botão de tentar de novo que funciona, e
// a garantia de que ao desmontar a sessão fecha — mesmo que a entrada ainda
// esteja a meio.

export const ESTADO_LIGACAO = {
  INATIVO: 'inativo',
  A_ENTRAR: 'a-entrar',
  LIGADO: 'ligado',
  ERRO: 'erro',
};

export function useSessaoAgora(etiqueta) {
  // A sessão é criada uma única vez por montagem e vive num ref: se estivesse
  // no estado, cada render trocava-a e perdíamos o cliente a meio da partida.
  const sessaoRef = useRef(null);
  if (!sessaoRef.current) sessaoRef.current = criarSessaoAgora(etiqueta);

  const [estado, setEstado] = useState(ESTADO_LIGACAO.INATIVO);
  const [codigoErro, setCodigoErro] = useState(null);
  const [tentativa, setTentativa] = useState(0);

  // Serve para não mexer no estado do React depois do ecrã sair — senão o
  // resultado de uma entrada abortada escrevia num componente desmontado.
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    const sessao = sessaoRef.current;
    return () => {
      montadoRef.current = false;
      // Fecha tudo: pistas, handlers, canal e os onSnapshot registados.
      sessao.terminar();
    };
  }, []);

  const entrar = useCallback(async (opcoes) => {
    if (montadoRef.current) {
      setEstado(ESTADO_LIGACAO.A_ENTRAR);
      setCodigoErro(null);
    }

    const resultado = await sessaoRef.current.entrar(opcoes);
    if (!montadoRef.current) return resultado;

    if (resultado.ok) {
      setEstado(ESTADO_LIGACAO.LIGADO);
      setCodigoErro(null);
    } else if (resultado.abortado || resultado.ocupado) {
      // Não é falha nenhuma: outra entrada tomou conta desta sessão (StrictMode,
      // ou uma revanche). Quem mandou essa é que decide o estado do ecrã.
    } else {
      setCodigoErro(resultado.codigo);
      setEstado(ESTADO_LIGACAO.ERRO);
    }
    return resultado;
  }, []);

  const terminar = useCallback(() => sessaoRef.current.terminar(), []);

  // Muda o contador de tentativas; o efeito de entrada do ecrã tem-no nas
  // dependências, por isso volta a correr sozinho.
  const tentarDeNovo = useCallback(() => {
    setCodigoErro(null);
    setEstado(ESTADO_LIGACAO.A_ENTRAR);
    setTentativa((n) => n + 1);
  }, []);

  const registarListener = useCallback((unsub) => sessaoRef.current.registarListener(unsub), []);

  return {
    sessao: sessaoRef.current,
    estado,
    codigoErro,
    mensagemErro: codigoErro ? mensagemDoErro(codigoErro) : null,
    entrar,
    terminar,
    tentativa,
    tentarDeNovo,
    registarListener,
  };
}
